import { useDndMonitor, useDroppable } from "@dnd-kit/core";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import {
  Box,
  BoxProps,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { Accept } from "react-dropzone";
import { useBoolean } from "usehooks-ts";

import { AddToListButton } from "@/components/ui-v2/AddToListButton";
import { UploadableModality } from "@/constants/dropzone";
import { useShowSnackbar } from "@/hooks";
import { useDropzoneFileUpload } from "@/hooks/useDropzoneFileUpload";

import { ContainerLoader } from "../ContainerLoader/ContainerLoader";
import { FilepickerFile, FilesLayoutStrategy } from "../FilePicker/types";
import { Icon } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import { ImagePreviewTile } from "../Tiles/ImagePreviewTile/ImagePreviewTile";
import { classes as tileBaseClasses } from "../Tiles/TileBase/TileBase";
import {
  FileSelectPreviewListTile,
  FileSelectPreviewListTileProps,
} from "./components/FileSelectPreviewListTile";
import { GroupedFilesOverlay } from "./components/GroupedFilesOverlay/GroupedFilesOverlay";

export const classes = {
  fileListContainer: "FileSelectPreviewList-file-list-container",
  fileListInDialogContainer:
    "FileSelectPreviewList-file-list-in-dialog-container",
};

import type { FileSelectPreviewFile } from "./types";

interface FileSelectPreviewListProps extends BoxProps {
  files: FileSelectPreviewFile[];
  onOpenFilePicker: () => void;
  onUpload: (file: FilepickerFile[]) => void;
  onDeleteFile: (index: number) => void;
  onEditFileCrop?: (index: number) => void;
  renderFooter?: (
    file: FileSelectPreviewFile,
    index: number,
  ) => React.ReactNode;
  addFileLabel?: string;
  accept?: Accept;
  maxFiles?: number;
  maxVisibleFiles?: number;
  filesLayout?: FilesLayoutStrategy;
  loading?: boolean;
  disabled?: boolean;
  allowUploadButtonCollapse?: boolean;
  isDroppableEnabled?: boolean;
  droppableId?: string;
  droppableAccepts?: string[];
  onDropped?: (item: any) => void;
  modality?: UploadableModality;
  uploadTestId?: string;
}

const GridFileListContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gridAutoRows: "1fr",
  gap: theme.spacing(3),
  width: "100%",
}));

const StackFileListContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  width: "100%",
}));

const StyledFileSelectPreviewListTile = styled(
  FileSelectPreviewListTile,
)<FileSelectPreviewListTileProps>(({ theme, renderFooter }) => ({
  [`& .${tileBaseClasses.root}`]: {
    marginBottom: !renderFooter ? 0 : theme.spacing(2),
    marginTop: !renderFooter ? 0 : theme.spacing(2),
  },
}));

export const FileSelectPreviewList = (props: FileSelectPreviewListProps) => {
  const {
    files,
    accept,
    maxFiles,
    loading: externalLoading,
    disabled: externalDisabled,
    onUpload,
    onOpenFilePicker,
    onDeleteFile,
    onEditFileCrop,
    renderFooter = () => null,
    addFileLabel,
    maxVisibleFiles,
    filesLayout = "grid",
    allowUploadButtonCollapse = true,
    isDroppableEnabled = false,
    droppableAccepts = [],
    onDropped,
    droppableId,
    modality,
    uploadTestId,
  } = props;
  const { showError } = useShowSnackbar();

  const {
    value: dialogOpen,
    setTrue: openDialog,
    setFalse: closeDialog,
  } = useBoolean();

  const dropzoneDisabled = maxFiles !== undefined && files.length >= maxFiles;

  const { getRootProps, getInputProps, isDragActive, isUploading } =
    useDropzoneFileUpload({
      onUploadSuccess: onUpload,
      accept,
      maxFiles,
      modality,
      disabled: dropzoneDisabled,
      noClick: true,
      noKeyboard: true,
    });

  const { setNodeRef, isOver } = useDroppable({
    id: droppableId ?? "file-select-preview-list",
    disabled: dropzoneDisabled || !isDroppableEnabled,
    data: {
      accepts: droppableAccepts,
    },
  });

  useDndMonitor({
    onDragEnd(event) {
      const { over, active } = event;

      if (over?.id === droppableId && active?.data?.current) {
        const { item, type } = active.data.current;

        if (item && type && over?.data?.current?.accepts?.includes(type)) {
          onDropped?.(item);
        } else {
          showError(
            "The file you dropped isn’t in a supported format. Please try again.",
          );
        }
      }
    },
  });

  const isLoading = isUploading || externalLoading;
  const isAtMaxFiles = maxFiles !== undefined && files.length >= maxFiles;

  const isMoreFilesThanCanBeShown =
    maxVisibleFiles && files.length > maxVisibleFiles;
  const visibleCount = isMoreFilesThanCanBeShown
    ? maxVisibleFiles
    : files.length;
  const hiddenCount = files.length - visibleCount;
  const visibleFiles = files.slice(0, visibleCount);

  const handleAction = (
    event: React.MouseEvent,
    action: string,
    index: number,
  ) => {
    switch (action) {
      case "DELETE":
        onDeleteFile(index);
        break;
      case "EDIT":
        onEditFileCrop?.(index);
        break;
      default:
        break;
    }
  };

  const Container =
    filesLayout === "grid" ? GridFileListContainer : StackFileListContainer;

  return (
    <Box ref={setNodeRef} sx={{ width: "100%" }}>
      <Container {...getRootProps()} className={classes.fileListContainer}>
        {visibleFiles.map((file, index) => {
          if (index === visibleFiles.length - 1 && hiddenCount > 0) {
            return (
              <GroupedFilesOverlay
                key={index}
                count={hiddenCount + 1}
                onClick={openDialog}
              >
                <ImagePreviewTile src={file.thumbnailSrc} alt="Image Preview" />
              </GroupedFilesOverlay>
            );
          }

          return (
            <StyledFileSelectPreviewListTile
              key={index}
              file={file}
              onAction={(event, action) => handleAction(event, action, index)}
              renderFooter={() => renderFooter(file, index)}
              isEditable={!!onEditFileCrop}
            />
          );
        })}
        {isLoading && (
          <Box sx={{ py: 4 }}>
            <ContainerLoader />
          </Box>
        )}
        {!isLoading && !isAtMaxFiles && (
          <AddToListButton
            isSupportingDrop
            data-testid={uploadTestId || "btn-add-file"}
            disabled={dropzoneDisabled || externalDisabled}
            onClick={onOpenFilePicker}
            isCollapsed={allowUploadButtonCollapse}
            isDragActive={isDragActive}
            isOver={isOver}
          >
            <input {...getInputProps()} />
            {addFileLabel ?? (
              <Typography variant="small">{addFileLabel}</Typography>
            )}
          </AddToListButton>
        )}
      </Container>
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            p: 4,
          }}
        >
          <Typography
            variant="large"
            sx={{
              fontWeight: "bold",
            }}
          >
            All Files ({files.length})
          </Typography>
          <IconButton
            size="large"
            aria-label="Close Dialog"
            onClick={closeDialog}
            variant="quaternary"
          >
            <Icon icon={faXmark} />
          </IconButton>
        </Stack>
        <Divider />
        <DialogContent>
          <GridFileListContainer className={classes.fileListInDialogContainer}>
            {files.map((file, index) => {
              return (
                <FileSelectPreviewListTile
                  key={index}
                  file={file}
                  onAction={(event, action) =>
                    handleAction(event, action, index)
                  }
                  isEditable={!!onEditFileCrop}
                />
              );
            })}
          </GridFileListContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
